import React, { useState, useRef, useEffect } from 'react';
import VariablePicker from '../components/variable_Picker';
import { useVariables } from '../contexts/VariableContext';
import { resolveVariables } from '../utils/variableResolver';
import {config} from '../config';

const SimpleInputField = ({ 
  generatedKey,
  placeholder = "Enter variable...", 
  className = ""
}) => {
  const { variableDict, updateVariable, updateTrigger } = useVariables();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const editorRef = useRef(null);
  const lastTriggerRef = useRef(updateTrigger);
  const [isFocused, setIsFocused] = useState(false);

  const colors = config.colors;

  // Listen for variable updates and refresh badges with their current values
  useEffect(() => {
    if (updateTrigger !== lastTriggerRef.current && editorRef.current) {
      lastTriggerRef.current = updateTrigger;
      
      // Update all variable badges in this field to show current values
      const badges = editorRef.current.querySelectorAll('[data-variable]');
      badges.forEach(badge => {
        const varKey = badge.dataset.variable;
        if (variableDict[varKey]) {
          badge.textContent = String(variableDict[varKey].value || varKey);
        }
      });
      
      // Update this field's stored value with current references intact
      // This ensures the field's value is always up-to-date
      if (generatedKey && variableDict[generatedKey]) {
        const rawValue = getValue(); // Get @variable syntax
        // Only update if it's actually different to avoid infinite loops
        if (variableDict[generatedKey].value !== rawValue) {
          updateVariable(generatedKey, rawValue);
        }
      }
    }
  }, [updateTrigger, variableDict]);

  const handleInput = (e) => {
    // Store the raw value with variable references (@variable syntax)
    const rawValue = getValue();
    
    // Update the dictionary with the raw reference format
    if (generatedKey && variableDict[generatedKey]) {
      updateVariable(generatedKey, rawValue);
    }
    
    // Check if @ is typed (for variables)
    const text = e.target.textContent;
    if (text.endsWith('@')) {
      setShowPicker(true);
      setSearchTerm('');
      
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setPickerPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX
      });
    } else if (showPicker) {
      const atIndex = text.lastIndexOf('@');
      if (atIndex !== -1) {
        const term = text.substring(atIndex + 1);
        setSearchTerm(term);
      } else {
        setShowPicker(false);
      }
    }
  };

  const insertVariable = (varKey) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const text = editor.textContent;
    const atIndex = text.lastIndexOf('@');
    
    editor.innerHTML = '';
    
    const beforeAt = text.substring(0, atIndex);
    
    if (beforeAt) {
      const textNode = document.createTextNode(beforeAt);
      editor.appendChild(textNode);
    }
    
    // Create badge that displays the value but stores the reference
    const badge = document.createElement('span');
    badge.contentEditable = 'false';
    badge.className = `inline-flex items-center px-2 py-0.5 mx-0.5 rounded text-sm font-medium cursor-default`;
    badge.style.backgroundColor = colors.nodeBg;
    badge.style.border = `1px solid ${colors.nodeBorder}`;
    badge.textContent = String(variableDict[varKey]?.value || varKey);
    badge.dataset.variable = varKey; // This stores the reference!
    badge.title = `@${varKey}`;
    editor.appendChild(badge);
    
    const spaceNode = document.createTextNode('');
    editor.appendChild(spaceNode);
    
    const range = document.createRange();
    range.setStartAfter(spaceNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    
    setShowPicker(false);
    setSearchTerm('');
    editor.focus();
    
    // Update dictionary with the raw reference format (@variable)
    const newRawValue = getValue();
    if (generatedKey) {
      updateVariable(generatedKey, newRawValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }
    
    if (showPicker && ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)) {
      return;
    }
    
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      if (range.collapsed) {
        const node = range.startContainer;
        const offset = range.startOffset;
        
        if (node.previousSibling && node.previousSibling.dataset && node.previousSibling.dataset.variable && offset === 0) {
          e.preventDefault();
          node.previousSibling.remove();
          
          const newRawValue = getValue();
          if (generatedKey) {
            updateVariable(generatedKey, newRawValue);
          }
        }
      }
    }
  };

  const clear = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
      editorRef.current.focus();
      
      if (generatedKey) {
        updateVariable(generatedKey, '');
      }
    }
  };

  // Get value WITH variable references (@variable syntax)
  // This is what gets stored
  const getValue = () => {
    if (!editorRef.current) return '';
    
    let result = '';
    const children = editorRef.current.childNodes;
    
    children.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent;
      } else if (node.dataset && node.dataset.variable) {
        // Store as @variable reference
        result += `@${node.dataset.variable}`;
      }
    });
    
    return result.trim();
  };

  // Get the RESOLVED value (with actual variable values substituted)
  // This is what you'd use for output/display
  const getActualValue = () => {
    const rawValue = getValue();
    return resolveVariables(rawValue, variableDict);
  };

  // Filter out current field's key from variables
  const getFilteredVariables = () => {
    const { [generatedKey]: _, ...filteredDict } = variableDict;
    return filteredDict;
  };

  return (
    <div className="relative">
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`min-h-[60px] px-4 py-3 rounded-lg focus:outline-none transition-colors overflow-x-auto ${className}`}
        style={{ 
          whiteSpace: 'nowrap',
          wordBreak: 'normal',
          overflowY: 'hidden',
          backgroundColor: colors.fieldBg,
          border: `0 solid ${isFocused ? colors.accent : colors.fieldBorder}`
        }}
        suppressContentEditableWarning
      />
      
      {editorRef.current && editorRef.current.textContent === '' && (
        <div 
          className="absolute top-3 left-4 pointer-events-none font-mono"
          style={{ color: colors.edges }}
        >
          {placeholder}
        </div>
      )}
      
      {showPicker && (
        <VariablePicker
          variables={getFilteredVariables()}
          position={pickerPosition}
          searchTerm={searchTerm}
          onSelect={insertVariable}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

export default SimpleInputField;